// src/navigation/sections.js
//
// The two product sections that are more than a list of pages. Each is one
// entry in the sidebar and carries its own navigation inside the page:
//
//  - the Feasibility Study is a workflow, so it is a stepper;
//  - Operations is a set of views grouped by what the operator is doing, so
//    it is a two-level tab bar (area → view).
//
// Defined once here so the sidebar, the section layouts and any "open in…"
// link agree on paths and labels.

import { isBtmSettlement } from "../components/config/btm";

export const FEASIBILITY_STEPS = [
  { key: "general", label: "Study Info", short: "Study", to: "/feasibility/general", hint: "Client, engineer and report metadata" },
  { key: "weather", label: "Weather Data", short: "Weather", to: "/feasibility/weather", hint: "Typical meteorological year for the site" },
  { key: "technical", label: "Technical Assumptions", short: "Technical", to: "/feasibility/technical", hint: "Demand, tariffs, dispatch assumptions" },
  { key: "economics", label: "Financial Assumptions", short: "Financial", to: "/feasibility/economics", hint: "Costs, financing, revenue streams" },
  { key: "simulation", label: "Simulation & Reports", short: "Simulate", to: "/feasibility/simulation", hint: "Run the study and produce the reports" },
];

// Analyses of the Feasibility workspace: optional tools that sit beside the
// workflow, not in it. They never change the five steps, their counter or
// Back / Next. Each says which projects it applies to (`applies`) and what to
// tell the user when it does not (`unavailable`), so the sidebar can list it
// disabled with an explanation rather than hide it. Every account has them.
// Asset types as the engine names them (pv_core/asset_types.py): "Hybrid" is
// PV with a battery. Labels that spell PV out still count.
const PV_ASSET_TYPES = new Set(["standalone pv", "hybrid"]);
const BATTERY_ASSET_TYPES = new Set(["hybrid", "hybrid wind", "standalone bess"]);
const assetType = (cfg) => String(cfg?.systemType || "").trim().toLowerCase();

export function projectHasPv(cfg) {
  const type = assetType(cfg);
  return PV_ASSET_TYPES.has(type) || /(^|[^a-z])pv([^a-z]|$)/.test(type);
}

export function projectHasBattery(cfg) {
  const type = assetType(cfg);
  return BATTERY_ASSET_TYPES.has(type) || /bess|battery/.test(type);
}

// Standalone PV and hybrid (PV + battery) projects alike: a hybrid's PV is
// sized on its own, without the battery (the page says so).
export function projectSupportsPvSizing(cfg) {
  return isBtmSettlement(cfg?.settlementType) && projectHasPv(cfg);
}

export const FEASIBILITY_ANALYSES = [
  {
    key: "pvSizing", label: "PV Sizing", to: "/feasibility/pv-sizing",
    hint: "Economic sizing of the self-consumption PV plant",
    applies: projectSupportsPvSizing,
    unavailable: "Available for behind-the-meter (self-consumption) projects with PV, standalone or hybrid.",
  },
];

// Asset Monitoring: the physical layer of the Operations workspace - live
// production, expected production and alarms of the operating PV parks. Its
// own routes (/monitoring/*) and its own access, so a client who only
// monitors parks is never blocked by the market-operations gate.
export const MONITORING_VIEWS = [
  { key: "portfolio", label: "Portfolio", to: "/monitoring" },
  { key: "alarms", label: "Alarms", to: "/monitoring/alarms" },
  { key: "reports", label: "Monthly Report", to: "/monitoring/reports" },
  { key: "sources", label: "Data Sources", to: "/monitoring/sources" },
];

export const OPERATIONS_AREAS = [
  {
    key: "desk",
    label: "Desk",
    views: [{ key: "desk", label: "Operations Desk", to: "/ops/desk" }],
  },
  {
    key: "trading",
    label: "Trading",
    views: [
      { key: "positions", label: "Positions", to: "/ops/positions" },
      { key: "orders", label: "Orders", to: "/ops/orders" },
      { key: "reserves", label: "Reserves", to: "/ops/reserves" },
    ],
  },
  {
    key: "optimisation",
    label: "Optimisation",
    views: [
      { key: "passes", label: "Pass Planner", to: "/ops/passes" },
      { key: "optimize", label: "Daily Optimization", to: "/optimize" },
      { key: "runs", label: "Historic Runs", to: "/dashboard?view=optimization" },
      { key: "bids", label: "Bid Schedules", to: "/dashboard?view=bid_schedule" },
    ],
  },
  {
    key: "reporting",
    label: "Reporting",
    views: [
      { key: "settlement", label: "Settlement & Performance", to: "/ops/settlement" },
      { key: "inputs", label: "Inputs & Forecasts", to: "/ops/inputs" },
    ],
  },
  {
    key: "setup",
    label: "Setup",
    views: [
      { key: "assets", label: "Portfolio & Onboarding", to: "/ops/assets" },
      { key: "settings", label: "Participation Settings", to: "/ops/settings" },
    ],
  },
];

/** Does `to` match the current location (path + the query keys it names)? */
export function routeMatches(to, location) {
  const [path, query = ""] = String(to).split("?");
  if (location.pathname !== path) return false;
  if (!query) return true;
  const cur = new URLSearchParams(location.search);
  return Array.from(new URLSearchParams(query).entries()).every(([k, v]) => cur.get(k) === v);
}

/** Is the location anywhere inside Operations? */
export function isMonitoringLocation(location) {
  return location.pathname === "/monitoring" || location.pathname.startsWith("/monitoring/");
}

export function isOperationsLocation(location) {
  if (isMonitoringLocation(location)) return true;
  if (location.pathname === "/ops" || location.pathname.startsWith("/ops/")) return true;
  if (location.pathname === "/optimize") return true;
  if (location.pathname === "/dashboard") {
    const view = new URLSearchParams(location.search).get("view");
    return view === "optimization" || view === "bid_schedule";
  }
  return false;
}

/** Shared project/data pages belong to the workspace the user selected. */
export function workspaceForLocation(location) {
  if (isOperationsLocation(location)) return "operations";
  if (location.pathname === "/feasibility" || location.pathname.startsWith("/feasibility/")) return "feasibility";
  return null;
}

export function currentOperationsView(location) {
  for (const area of OPERATIONS_AREAS) {
    for (const view of area.views) {
      if (routeMatches(view.to, location)) return { area, view };
    }
  }
  return null;
}
